import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { Plus, RefreshCw, Edit3 } from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import CuentaForm from './Form.jsx';
import CuentaDetail from './Detail.jsx';
import { ARBOL_CUENTAS, LIST_CUENTAS, ACTIVAR_CUENTA } from './gql.js';
import { useMutation } from 'urql';

function filterTree(nodes, term) {
  if (!term) {
    return nodes;
  }
  const search = term.toLowerCase();
  const iterate = (list) =>
    list
      .map((node) => {
        const hijos = node.hijos ? iterate(node.hijos) : [];
        const matches = `${node.codigo} ${node.nombre}`.toLowerCase().includes(search);
        if (matches || hijos.length > 0) {
          return { ...node, hijos };
        }
        return null;
      })
      .filter(Boolean);
  return iterate(nodes);
}

function flattenTree(nodes, parent = null, acc = []) {
  for (const node of nodes) {
    acc.push({ ...node, padreLabel: parent ? `${parent.codigo} · ${parent.nombre}` : null });
    if (node.hijos && node.hijos.length) {
      flattenTree(node.hijos, node, acc);
    }
  }
  return acc;
}

export default function CuentasList() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('view');
  const [activos, setActivos] = useState(true);
  const [treeResult, reexecuteTree] = useQuery({
    query: ARBOL_CUENTAS,
    variables: { activo: activos ? true : null },
  });
  const [listResult, reexecuteList] = useQuery({
    query: LIST_CUENTAS,
    variables: { limit: 500, offset: 0 },
  });
  const [, activarCuenta] = useMutation(ACTIVAR_CUENTA);

  const cuentasTree = treeResult.data?.arbolCuentas ?? [];
  const treeNodes = useMemo(() => flattenTree(cuentasTree), [cuentasTree]);
  const filteredTree = useMemo(() => filterTree(cuentasTree, search), [cuentasTree, search]);
  const cuentasFlat = useMemo(() => {
    const listItems = listResult.data?.listCuentas?.items ?? [];
    const map = new Map(treeNodes.map((node) => [node.id, node]));
    return listItems.map((item) => {
      const parentNode = item.padreId ? map.get(item.padreId) : null;
      return {
        ...item,
        padreLabel: parentNode ? `${parentNode.codigo} · ${parentNode.nombre}` : null,
      };
    });
  }, [listResult.data, treeNodes]);
  const selectedCuenta = useMemo(() => {
    return cuentasFlat.find((item) => item.id === selectedId) ?? treeNodes.find((item) => item.id === selectedId) ?? null;
  }, [cuentasFlat, treeNodes, selectedId]);

  const handleSelect = (node) => {
    setSelectedId(node.id);
    setMode('view');
  };

  const handleCrear = () => {
    setMode('create');
  };

  const handleEditar = () => {
    if (!selectedCuenta) {
      toast.info('Seleccioná una cuenta para editar');
      return;
    }
    setMode('edit');
  };

  const handleRefresh = () => {
    reexecuteTree({ requestPolicy: 'network-only' });
    reexecuteList({ requestPolicy: 'network-only' });
  };

  const handleSuccess = () => {
    setMode('view');
    handleRefresh();
  };

  const handleToggleActiva = async (node) => {
    try {
      const result = await activarCuenta({ id: node.id, activo: !node.activo });
      if (result.error) {
        throw result.error;
      }
      toast.success(`Cuenta ${!node.activo ? 'activada' : 'desactivada'}`);
      handleRefresh();
    } catch (error) {
      toast.error(error.message ?? 'No fue posible actualizar la cuenta');
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.9)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Plan de Cuentas"
          subtitle="Gestioná la estructura contable de la empresa"
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizar
            </button>,
            <button
              key="edit"
              type="button"
              onClick={handleEditar}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <Edit3 className="h-4 w-4" />
              Editar
            </button>,
            <button
              key="create"
              type="button"
              onClick={handleCrear}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              <Plus className="h-4 w-4" />
              Nueva cuenta
            </button>,
          ]}
        />

        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-12 md:col-span-4">
            <section className="flex h-full flex-col gap-4 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-4 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
              <div className="flex flex-col gap-3 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.6)] p-3">
                <label className="text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]">
                  Buscar cuenta
                </label>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Código o nombre"
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                />
                <label className="flex items-center gap-2 text-xs text-[rgb(var(--fg))]">
                  <input
                    type="checkbox"
                    checked={activos}
                    onChange={(event) => setActivos(event.target.checked)}
                    className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                  />
                  Mostrar solo activas
                </label>
              </div>

              <div className="flex-1 overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.45)]">
                {treeResult.fetching ? (
                  <div className="flex h-full items-center justify-center text-sm text-[rgb(var(--muted))]">Cargando árbol...</div>
                ) : filteredTree.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-[rgb(var(--muted))]">Sin resultados</div>
                ) : (
                  <div className="max-h-[600px] overflow-auto px-2 py-3">
                    <Tree
                      nodes={filteredTree}
                      selectedId={selectedId}
                      onSelect={handleSelect}
                      onToggleActivo={handleToggleActiva}
                    />
                  </div>
                )}
              </div>
            </section>
          </aside>

          <main className="col-span-12 md:col-span-8 space-y-4">
            {mode === 'create' ? (
              <CuentaForm cuentas={cuentasFlat} onSuccess={handleSuccess} onCancel={() => setMode('view')} />
            ) : mode === 'edit' && selectedCuenta ? (
              <CuentaForm
                cuenta={selectedCuenta}
                cuentas={cuentasFlat}
                onSuccess={handleSuccess}
                onCancel={() => setMode('view')}
              />
            ) : (
              <CuentaDetail cuenta={selectedCuenta} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function Tree({ nodes, selectedId, onSelect, onToggleActivo, depth = 0 }) {
  if (!nodes?.length) return null;
  return (
    <ul className="space-y-1">
      {nodes.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          selectedId={selectedId}
          onSelect={onSelect}
          onToggleActivo={onToggleActivo}
          depth={depth}
        />
      ))}
    </ul>
  );
}

function TreeNode({ node, selectedId, onSelect, onToggleActivo, depth }) {
  const [expanded, setExpanded] = useState(true);
  const isSelected = node.id === selectedId;
  const hasChildren = node.hijos && node.hijos.length > 0;

  return (
    <li>
      <div
        className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm transition-colors ${
          isSelected
            ? 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]'
            : 'hover:bg-[rgba(var(--muted),0.12)] text-[rgb(var(--fg))]'
        }`}
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          onClick={() => {
            onSelect(node);
            if (hasChildren) {
              setExpanded((prev) => !prev);
            }
          }}
          className="flex flex-1 items-center gap-3 text-left focus:outline-none"
        >
          {hasChildren ? (
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full border border-[rgb(var(--border))] text-[10px] font-semibold ${
                expanded ? 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]' : 'text-[rgb(var(--muted))]'
              }`}
            >
              {expanded ? '-' : '+'}
            </span>
          ) : (
            <span className="h-5 w-5" aria-hidden />
          )}
          <div className="flex flex-col">
            <span className="font-medium">
              {node.codigo} · {node.nombre}
            </span>
            <span className="text-xs text-[rgb(var(--muted))]">
              {node.imputable ? 'Imputable' : 'No imputable'} · {node.tipo}
            </span>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onToggleActivo(node)}
          className={`rounded-full px-3 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
            node.activo
              ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand-700))]'
              : 'bg-[rgba(var(--muted),0.2)] text-[rgb(var(--muted))]'
          }`}
        >
          {node.activo ? 'Activa' : 'Inactiva'}
        </button>
      </div>
      {hasChildren && expanded ? (
        <div className="mt-1 border-l border-dashed border-[rgba(var(--border),0.6)] pl-3">
          <Tree
            nodes={node.hijos}
            selectedId={selectedId}
            onSelect={onSelect}
            onToggleActivo={onToggleActivo}
            depth={depth + 1}
          />
        </div>
      ) : null}
    </li>
  );
}

